"use client";

// Documentation content - English. Mirrors public/documentation-en.html.
// Concise edition: the essentials, the admin key, nothing else.

import {
  Callout,
  DocBadge,
  DocTable,
  L,
  OL,
  P,
  type DocSection,
} from "./primitives";

export const sectionsEN: DocSection[] = [
  // ---- Part 1 · Start here ----
  {
    id: "what-is-homebook",
    num: "1.1",
    part: 1,
    title: "What is HomeBook?",
    body: (
      <>
        <P>
          HomeBook is a shared family calendar for our houses. Every house has a
          page with photos, prices and a calendar. You pick your days, the people
          in charge say yes or no, and everyone sees who is staying.
        </P>
        <P>It works on any phone, tablet or computer - nothing to install.</P>
      </>
    ),
  },
  {
    id: "sign-in",
    num: "1.2",
    part: 1,
    title: "Sign in & admin access",
    body: (
      <>
        <OL>
          <li>Open the site and press <strong>Sign in</strong> (top right).</li>
          <li>Type your username and password. Press the button. Done.</li>
        </OL>
        <Callout tone="warn" label="Admin access">
          <P>
            Username <code>admin</code> - password <code>{"admin" + "123"}</code>.
            The admin can do everything and can set a new password for any
            account. <strong>Change this password</strong> before the family
            really uses the site.
          </P>
        </Callout>
        <P>
          No account yet? An admin creates one for you in under a minute - or
          pick up the link they sent you.
        </P>
      </>
    ),
  },
  {
    id: "privacy",
    num: "1.3",
    part: 1,
    title: "Your data stays private",
    body: (
      <>
        <P>
          Personal details (names, e-mail addresses, phone numbers) are stored
          encrypted and are <strong>never shown in full</strong> in the app.
          You will see partial forms such as <code>Davy J.</code> or{" "}
          <code>d•••@g•••.com</code>.
        </P>
        <Callout tone="info" label="Good to know">
          <P>
            To change someone's real details, an admin retypes the full value in
            the member form - typing a new value replaces the old one.
          </P>
        </Callout>
      </>
    ),
  },

  // ---- Part 2 · The main flow ----
  {
    id: "how-it-works",
    num: "2.1",
    part: 2,
    title: "How it works",
    body: (
      <>
        <P>
          The app starts on the <strong>Home screen</strong>: big cards, one per
          destination. Tap a card to open that part of the app, and use the{" "}
          <strong>Back</strong> arrow (top left) whenever you want to go up one
          level — the HomeBook logo always brings you back Home.
        </P>
        <OL>
          <li>Tap <strong>Browse houses</strong>, then open a house.</li>
          <li>Pick your days on its calendar - green means free.</li>
          <li>
            Say how many people are coming, and choose{" "}
            <strong>Private</strong> (whole house) or <strong>Shared</strong>.
          </li>
          <li>
            <strong>Private stays can be shared between members:</strong> add the
            people who are coming and the price is split equally between them
            (you can see each person's share on the stay). Once the host
            approves the stay, the list is locked — only an admin or the owner
            can still change it.
          </li>
          <li>
            Send the request. It waits as <strong>pending</strong> until the
            owner or house staff approve it.
          </li>
          <li>
            Approved? Your name shows on those days and everything you booked
            lives under <strong>My Trips</strong>.
          </li>
        </OL>
        <Callout tone="note" label="Shortcut">
          <P>
            You can also press a green day on your <strong>Dashboard</strong>{" "}
            calendar — the same booking form appears right below the calendar.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "calendar-colors",
    num: "2.2",
    part: 2,
    title: "The calendar colors",
    body: (
      <>
        <DocTable
          head={["Color", "Meaning"]}
          rows={[
            [
              <span key="g" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500" />
                Green
              </span>,
              "Free. You can ask for this day.",
            ],
            [
              <span key="r" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-rose-500" />
                Red
              </span>,
              "The whole house is taken (private booking).",
            ],
            [
              <span key="b" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-sky-400" />
                Blue
              </span>,
              "Shared stay - several people in the house.",
            ],
            [
              <span key="o" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-dashed border-amber-500" />
                Orange
              </span>,
              "Requested, waiting for approval.",
            ],
            [
              <span key="y" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-300" />
                Grey
              </span>,
              "The house is closed that day.",
            ],
            [
              <span key="d" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-400" />
                Dark grey
              </span>,
              "In the past - the day is over.",
            ],
          ]}
        />
        <P>
          Press any day to see who is there. Owners and house staff get{" "}
          <strong>Approve</strong> / <strong>Decline</strong> buttons for waiting
          requests.
        </P>
      </>
    ),
  },
  {
    id: "my-trips",
    num: "2.3",
    part: 2,
    title: "Your trips & cancelling",
    body: (
      <>
        <P>
          Everything you booked lives under <strong>My Trips</strong>. A request
          that is still <strong>pending</strong> can be cancelled by you at any
          time; an <strong>approved</strong> stay is cancelled by the owner or an
          admin - just ask.
        </P>
      </>
    ),
  },

  // ---- Part 3 · Owners, staff & money ----
  {
    id: "who-does-what",
    num: "3.1",
    part: 3,
    title: "Who does what",
    body: (
      <>
        <P>Every account has a badge, and the badge decides what it can do:</P>
        <DocTable
          head={["Badge", "Can do"]}
          rows={[
            [
              <DocBadge key="v" tone="neutral">
                Visitor
              </DocBadge>,
              "Only look at the houses.",
            ],
            [
              <DocBadge key="u" tone="neutral">
                User
              </DocBadge>,
              "Book houses, see own trips, pay bills assigned to them.",
            ],
            [
              <DocBadge key="o" tone="violet">
                OWNER
              </DocBadge>,
              "Owns one or more houses: full control, approves requests, sees their bills.",
            ],
            [
              <DocBadge key="m" tone="sky">
                MODERATOR
              </DocBadge>,
              "A helper. Can be given one house or a permission to approve bookings.",
            ],
            [
              <DocBadge key="a" tone="dark">
                ADMIN
              </DocBadge>,
              "Everything: users, roles, houses, settings.",
            ],
          ]}
        />
      </>
    ),
  },
  {
    id: "manage-a-house",
    num: "3.2",
    part: 3,
    title: "Managing a house",
    body: (
      <>
        <P>
          Open a house page as its owner or as an admin: a{" "}
          <strong>manage bar</strong> appears at the top. Everything is editable
          right there - no separate admin area.
        </P>
        <L>
          <li>
            <strong>Edit</strong> - title, description, price, family discount.
          </li>
          <li>
            <strong>Availability</strong> - which days can be booked.
          </li>
          <li>
            <strong>Photos</strong> - or simply click any picture on the page to
            replace it.
          </li>
          <li>
            <strong>Season prices</strong> - higher rates for high-season dates.
          </li>
          <li>
            <strong>Hide / Publish</strong> - take a house off the public list
            and back.
          </li>
        </L>
        <P>
          Waiting requests appear on the house page and on your Dashboard
          calendar - open the day and press <strong>Approve</strong> or{" "}
          <strong>Decline</strong>.
        </P>
      </>
    ),
  },
  {
    id: "house-staff",
    num: "3.3",
    part: 3,
    title: "House staff & family discounts",
    body: (
      <>
        <P>
          Press <strong>House staff</strong> on a house page to assign a
          moderator to it: they can then approve bookings and handle the bills
          of <strong>that house only</strong> - they see nothing else.
        </P>
        <P>
          Houses also belong to <strong>family groups</strong>. Members of a
          house's family automatically pay the family discount there - you set
          the percentage when editing the house.
        </P>
      </>
    ),
  },
  {
    id: "bills-and-money",
    num: "3.4",
    part: 3,
    title: "Bills and money",
    body: (
      <>
        <P>
          House costs become <strong>invoices</strong>: a number, an amount, and
          one person who has to pay. The <strong>Expenses</strong> page keeps it
          all in order:
        </P>
        <P>
          The <strong>Dashboard</strong> follows the same house filter: pick one
          house (or all of them) and every figure and pie chart follows — nights
          per house and per member, expenses per house, paid per member and the
          share of the bills each member is responsible for.
        </P>
        <L>
          <li>
            The <strong>house switcher</strong> at the top filters every list
            and chart by house.
          </li>
          <li>
            Click a summary card (Overdue, Due soon, Paid) to jump to exactly
            those bills.
          </li>
          <li>
            Every waiting bill shows an <strong>assignment chip</strong> - press
            it to choose who pays.
          </li>
          <li>
            <strong>Mark paid</strong> records who paid and when; spending by
            month and by category is charted below.
          </li>
        </L>
      </>
    ),
  },

  // ---- Part 3 · Words & fixes ----
  {
    id: "words-and-fixes",
    num: "3.5",
    part: 3,
    title: "Words & quick fixes",
    body: (
      <>
        <DocTable
          head={["Word on the site", "What it means"]}
          rows={[
            ["Pending", "Sent, waiting for a yes."],
            ["Private / Shared", "Whole house yours / share it with others."],
            ["My Trips", "Your requests and stays."],
            ["Hide / Publish", "Take a house off / put it back on the public list."],
            ["House staff", "Moderators assigned to one house."],
            ["Invoice", "A bill: what, how much, who pays."],
            ["Assignment", "The person who has to pay a bill."],
          ]}
        />
        <L>
          <li>
            <strong>Forgot your password?</strong> The admin can set you a new
            one in seconds.
          </li>
          <li>
            <strong>A day refuses to be picked?</strong> It is taken or the
            house is closed. Try other days.
          </li>
          <li>
            <strong>Something looks broken?</strong> Leave the page and come
            back - that fixes most glitches.
          </li>
        </L>
      </>
    ),
  },
];
